import { BoxGeometry, BufferAttribute, BufferGeometry, CylinderGeometry, PlaneGeometry } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { fbm3 } from './builder';

export interface DoorwayParts {
  /** The stone frame: two weathered pillars, a lintel and a threshold. */
  stone: BufferGeometry;
  /** A plane filling the opening (uv 0..1 across it). */
  light: BufferGeometry;
  /** Soft rays fanning out of the opening, just in front of it. aAlong 0 at the source → 1 at the tip. */
  spill: BufferGeometry;
  /** Opening size (m). */
  width: number;
  height: number;
}

/**
 * A far-north landmark: a lone, enormous stone doorway with light pouring through it. Origin at
 * the centre of the threshold; the doorway faces +z (toward the player, who runs toward −z).
 */
export function doorway(height: number, seed = 3): DoorwayParts {
  const H = height * 0.82; // opening height
  const W = height * 0.27; // opening width
  const T = height * 0.085; // pillar thickness
  const L = height * 0.11; // lintel height
  const D = height * 0.1; // depth
  const parts: BufferGeometry[] = [];
  const slab = (cx: number, y0: number, w: number, h: number, d: number, segs: [number, number, number]): void => {
    const g = new BoxGeometry(w, h, d, ...segs);
    g.translate(cx, y0 + h / 2, 0);
    parts.push(g);
  };
  slab(-(W / 2 + T / 2), 0, T, H, D, [3, 24, 3]);
  slab(W / 2 + T / 2, 0, T, H, D, [3, 24, 3]);
  slab(0, H, W + T * 2 + height * 0.05, L, D * 1.08, [10, 3, 3]);
  // A low, broad threshold half-sunk into the ground.
  slab(0, -height * 0.06, W + T * 2.6, height * 0.075, D * 1.6, [10, 2, 3]);
  const stone = mergeGeometries(parts.map((p) => p.toNonIndexed()))!;
  for (const p of parts) p.dispose();
  // Weathering: displace by a smooth 3D noise of position (coincident vertices move together,
  // so slabs stay closed), stronger toward the edges of the pillars' faces.
  const pos = stone.getAttribute('position') as BufferAttribute;
  const k = 1 / (height * 0.08);
  const amp = height * 0.012;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    pos.setXYZ(
      i,
      x + (fbm3(x * k, y * k, z * k, seed, 3) - 0.5) * amp * 2,
      y + (fbm3(x * k + 7.1, y * k, z * k, seed, 3) - 0.5) * amp * 0.6,
      z + (fbm3(x * k, y * k + 3.3, z * k, seed, 3) - 0.5) * amp * 2,
    );
  }
  stone.computeVertexNormals();
  stone.computeBoundingSphere();

  const light = new PlaneGeometry(W * 1.02, H * 1.01);
  light.translate(0, H / 2, -D * 0.1);

  // The spill: soft rays fanning out of the opening, in a plane just in front of the door, so
  // the light seems to pour past the stone into the haze. aAlong 0 at the source → 1 at the tip.
  const q: number[] = [];
  const along: number[] = [];
  const across: number[] = [];
  const cy = H * 0.5;
  const zf = D * 0.7;
  let rs = seed * 9.73;
  const rnd = (): number => {
    rs = (rs * 16807 + 0.5) % 2147483647;
    return (rs % 10000) / 10000;
  };
  const RAYS = 28;
  for (let i = 0; i < RAYS; i++) {
    // All the way round, uneven lengths: a soft glory of light leaking past the stone.
    const ang = (i / RAYS) * Math.PI * 2 + (rnd() - 0.5) * 0.18;
    const len = H * (0.45 + rnd() * 0.75);
    const w0 = W * 0.05;
    const w1 = W * (0.1 + rnd() * 0.2);
    const dx = Math.cos(ang);
    const dy = Math.sin(ang) * 1.3;
    const l = Math.hypot(dx, dy);
    const ux = dx / l;
    const uy = dy / l;
    const px = -uy;
    const py = ux;
    const ax = ux * W * 0.2;
    const ay = cy + uy * H * 0.2;
    const tip = [ax + ux * len, ay + uy * len];
    const v = [
      [ax + px * w0, ay + py * w0, 0, 0],
      [ax - px * w0, ay - py * w0, 0, 1],
      [tip[0]! - px * w1, tip[1]! - py * w1, 1, 1],
      [tip[0]! + px * w1, tip[1]! + py * w1, 1, 0],
    ];
    for (const k of [0, 1, 2, 0, 2, 3]) {
      const p = v[k]!;
      q.push(p[0]!, p[1]!, zf);
      along.push(p[2]!);
      across.push(p[3]!);
    }
  }
  const spill = new BufferGeometry();
  spill.setAttribute('position', new BufferAttribute(new Float32Array(q), 3));
  spill.setAttribute('aAlong', new BufferAttribute(new Float32Array(along), 1));
  spill.setAttribute('aAcross', new BufferAttribute(new Float32Array(across), 1));
  spill.computeBoundingSphere();
  return { stone, light, spill, width: W, height: H };
}

export interface PeakParts {
  /** The hanging mountain. Origin at its tip; it rises to `height`. aU: 0 at the tip → 1 at the crown. */
  rock: BufferGeometry;
  /** The beam falling straight down from the tip: an open cylinder, y 0 (tip) → −1 (ground). */
  fall: BufferGeometry;
  /** Width across the crown (m). */
  width: number;
  height: number;
}

/**
 * The far-north landmark, take two: an inverted peak — a mountain hanging point-down over the
 * horizon, light falling from its tip to the ground. Flanks are concave like a real summit's,
 * ridged by noise that runs tip-to-crown; the crown (the mountain's old "base") is a low, broken
 * dome. Origin at the tip.
 */
export function invertedPeak(height: number, seed = 5): PeakParts {
  const R = height * 0.62;
  const sides = 112;
  const rings = 56;
  const crownRings = 10;
  const pos: number[] = [];
  const us: number[] = [];
  const idx: number[] = [];
  const stride = sides + 1;
  // Flanks: u 0 at the tip → 1 at the rim of the crown.
  for (let j = 0; j <= rings; j++) {
    const u = j / rings;
    for (let i = 0; i <= sides; i++) {
      const a = (i / sides) * Math.PI * 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      // Ridges running tip-to-crown, finer crags on top of them, and ledges where the rock
      // stepped as it broke away — a torn-up summit, not a smooth funnel.
      const ridge = 1 - Math.abs(fbm3(ca * 2.4 + seed, sa * 2.4, u * 2.2, seed, 4) * 2 - 1);
      const crag = fbm3(ca * 7 + seed, sa * 7, u * 9, seed + 7, 3) - 0.5;
      const ledge = Math.floor(u * 7 + fbm3(ca * 2, sa * 2, 1.3, seed + 2, 2) * 2) / 7;
      const lobes = 1 + 0.2 * Math.sin(a * 3 + seed) + 0.1 * Math.sin(a * 5 - seed * 2);
      const prof = Math.pow(u, 1.45) * 0.78 + Math.pow(ledge, 1.45) * 0.22;
      let r = R * prof * lobes * (0.72 + ridge * 0.5 + crag * 0.22) + R * 0.004;
      let y = u * height * (1 + (fbm3(ca * 3 + 11, sa * 3, u * 4, seed, 3) - 0.5) * 0.06);
      if (j === 0) {
        r = 0;
        y = 0;
      }
      pos.push(ca * r, y, sa * r);
      us.push(u);
    }
  }
  for (let j = 0; j < rings; j++) {
    for (let i = 0; i < sides; i++) {
      const a = j * stride + i;
      // Outward (and downward) facing: the flanks widen as they rise.
      idx.push(a, a + stride, a + 1, a + 1, a + stride, a + stride + 1);
    }
  }
  // Crown: from the rim up and inward to a low, broken dome.
  const rim = rings * stride;
  for (let k = 1; k <= crownRings; k++) {
    const t = k / crownRings;
    for (let i = 0; i <= sides; i++) {
      const p = rim + i;
      const x = pos[p * 3]!;
      const yRim = pos[p * 3 + 1]!;
      const z = pos[p * 3 + 2]!;
      const a = (i / sides) * Math.PI * 2;
      const shrink = Math.cos((t * Math.PI) / 2);
      const bump = (fbm3(Math.cos(a) * 2 * t + seed, Math.sin(a) * 2 * t, t * 3, seed + 3, 3) - 0.45) * height * 0.07;
      pos.push(x * shrink, yRim + Math.sin((t * Math.PI) / 2) * height * 0.1 + bump * Math.sin(t * Math.PI), z * shrink);
      us.push(1);
    }
    const prev = k === 1 ? rim : rim + stride + (k - 2) * stride;
    const cur = rim + stride + (k - 1) * stride;
    for (let i = 0; i < sides; i++) idx.push(prev + i, cur + i, prev + i + 1, prev + i + 1, cur + i, cur + i + 1);
  }
  // A few smaller spurs hanging off the lower flanks, like broken-off summits of their own.
  const SPURS = 5;
  const SR = 10;
  for (let k = 0; k < SPURS; k++) {
    const ang = (k / SPURS) * Math.PI * 2 + seed * 0.7 + (k % 2) * 0.4;
    const u0 = 0.32 + (k % 3) * 0.1;
    const ca = Math.cos(ang);
    const sa = Math.sin(ang);
    const hr = R * Math.pow(u0, 1.5) * 0.85;
    const len = height * (0.16 + (k % 3) * 0.05);
    const w = R * (0.09 + (k % 2) * 0.04);
    const base0 = pos.length / 3;
    const top = u0 * height + len * 0.25;
    // Rings from the spur's own tip (bottom) up into the flank.
    for (let j = 0; j <= 4; j++) {
      const t = j / 4;
      for (let i = 0; i <= SR; i++) {
        const b = (i / SR) * Math.PI * 2;
        const rr = w * Math.pow(t, 1.3) * (0.85 + 0.3 * fbm3(Math.cos(b) * 2 + k, Math.sin(b) * 2, t * 3, seed + k, 2));
        pos.push(ca * hr * 0.98 + Math.cos(b) * rr, top - len + t * len, sa * hr * 0.98 + Math.sin(b) * rr);
        us.push(0.35 + t * 0.4);
      }
    }
    for (let j = 0; j < 4; j++) {
      for (let i = 0; i < SR; i++) {
        const a0 = base0 + j * (SR + 1) + i;
        idx.push(a0, a0 + SR + 1, a0 + 1, a0 + 1, a0 + SR + 1, a0 + SR + 2);
      }
    }
  }
  const rock = new BufferGeometry();
  rock.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
  rock.setAttribute('aU', new BufferAttribute(new Float32Array(us), 1));
  rock.setIndex(idx);
  rock.computeVertexNormals();
  rock.computeBoundingSphere();

  // A beam straight down from the tip — parallel-sided, a marker pointing at a place on the ground.
  const fall = new CylinderGeometry(1, 1, 1, 32, 1, true);
  fall.translate(0, -0.5, 0);
  return { rock, fall, width: R * 2, height };
}

/** Open cylinder for a light beam (a friend's position in a shared run): unit radius, y from 0 to 1. */
export function beamGeometry(): BufferGeometry {
  const g = new CylinderGeometry(1, 1, 1, 24, 1, true);
  g.translate(0, 0.5, 0);
  return g;
}

/**
 * A ring of silhouettes on the horizon, centred on the camera: distant cumulus banks ('clouds')
 * or far dune ridges ('dunes'). aRow runs 0 at the base to 1 at the silhouette's top edge.
 */
export function horizonRing(radius: number, height: number, kind: 'clouds' | 'dunes', seed: number): BufferGeometry {
  const seg = 384;
  const rows = 10;
  const tops: number[] = [];
  if (kind === 'clouds') {
    // Cumulus: overlapping round bumps of different sizes along the ring.
    const bumps: { a: number; w: number; h: number }[] = [];
    let a = 0;
    let k = 0;
    while (a < Math.PI * 2) {
      const w = 0.025 + 0.05 * frac(Math.sin(seed * 13.1 + k * 7.7) * 4375.85);
      const h = 0.25 + 0.75 * Math.pow(frac(Math.sin(seed * 3.3 + k * 1.37) * 9137.2), 1.5);
      bumps.push({ a, w, h });
      a += w * (0.8 + frac(Math.sin(k * 5.1 + seed) * 777.7) * 0.9);
      k++;
    }
    const band = 0.5 + 0.5 * Math.sin(seed);
    for (let i = 0; i <= seg; i++) {
      const t = (i / seg) * Math.PI * 2;
      let y = 0.12;
      for (const b of bumps) {
        let d = Math.abs(t - b.a);
        d = Math.min(d, Math.PI * 2 - d);
        if (d < b.w) y = Math.max(y, b.h * Math.sqrt(1 - (d / b.w) * (d / b.w)));
      }
      // Banks come and go around the horizon.
      const gap = 0.35 + 0.65 * Math.pow(0.5 + 0.5 * Math.sin(t * 3 + band * 6) * Math.cos(t * 5 + seed), 1.2);
      tops.push(y * gap * height);
    }
  } else {
    for (let i = 0; i <= seg; i++) {
      const t = (i / seg) * Math.PI * 2;
      // Asymmetric dune ridges (long windward rise, short slip face) of mixed sizes.
      const ridge = (f: number, ph: number): number => {
        const x = (t * f) / (Math.PI * 2) + ph;
        const u = x - Math.floor(x);
        return u < 0.72 ? Math.pow(u / 0.72, 1.6) : Math.pow(1 - (u - 0.72) / 0.28, 0.7);
      };
      tops.push(height * (0.25 + 0.5 * ridge(23, seed) + 0.25 * ridge(61, seed * 1.7)));
    }
  }
  const pos: number[] = [];
  const row: number[] = [];
  const idx: number[] = [];
  const base = -height * 0.6;
  for (let r = 0; r <= rows; r++) {
    const f = r / rows;
    for (let i = 0; i <= seg; i++) {
      const t = (i / seg) * Math.PI * 2;
      const top = tops[i]!;
      const y = base + (top - base) * Math.pow(f, kind === 'clouds' ? 0.7 : 1);
      // Clouds bulge toward the viewer near their tops (rounded, not a flat card).
      const bulge = kind === 'clouds' ? Math.sin(f * Math.PI) * top * 0.15 : 0;
      const rr = radius - bulge;
      pos.push(Math.cos(t) * rr, y, Math.sin(t) * rr);
      row.push(f);
    }
  }
  const stride = seg + 1;
  for (let r = 0; r < rows; r++) {
    for (let i = 0; i < seg; i++) {
      const a = r * stride + i;
      // Facing inward (toward the camera at the centre).
      idx.push(a, a + 1, a + stride, a + 1, a + stride + 1, a + stride);
    }
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('aRow', new BufferAttribute(new Float32Array(row), 1));
  g.setIndex(idx);
  // Normals face the viewer, tilted by the silhouette's slope so ridge flanks catch or lose the sun.
  const nor = new Float32Array(pos.length);
  const dt = (Math.PI * 2) / seg;
  for (let r = 0; r <= rows; r++) {
    const f = r / rows;
    for (let i = 0; i <= seg; i++) {
      const t = i * dt;
      const slope = (tops[Math.min(seg, i + 1)]! - tops[Math.max(0, i - 1)]!) / (2 * dt * radius);
      const ix = -Math.cos(t);
      const iz = -Math.sin(t);
      const tx = -Math.sin(t);
      const tz = Math.cos(t);
      let nx = ix - tx * slope * 2.5 * f;
      let ny = 0.35 + 0.5 * f;
      let nz = iz - tz * slope * 2.5 * f;
      const l = Math.hypot(nx, ny, nz);
      nx /= l;
      ny /= l;
      nz /= l;
      const o = (r * stride + i) * 3;
      nor[o] = nx;
      nor[o + 1] = ny;
      nor[o + 2] = nz;
    }
  }
  g.setAttribute('normal', new BufferAttribute(nor, 3));
  g.computeBoundingSphere();
  return g;
}

function frac(x: number): number {
  return x - Math.floor(x);
}
